import { useState } from 'react';
import { Form, Input, Button, Typography, message, Select, Segmented } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, ArrowLeftOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

// Set VITE_LANDING_URL=http://localhost:5556 in admin/.env.local only if running
// the landing page as a separate dev server. For prod (same domain), leave unset.
const LANDING_URL = import.meta.env.VITE_LANDING_URL ?? '/';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { GRADES } from '@/constants';
import type { UserRoleValue, Board, Subject } from '@/types';

interface RegisterForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  parentEmail?: string;
  gradeIds?: number[];
  subjectIds?: string[];
}

interface RegisterResponse {
  data: {
    user: { id: string; name: string; email: string; role: string };
    accessToken: string;
    refreshToken: string;
  };
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  const [form] = Form.useForm();
  const initialRole = searchParams.get('role')?.toUpperCase() === 'TEACHER' ? 'TEACHER' : 'STUDENT';
  const [role, setRole] = useState<'TEACHER' | 'STUDENT'>(initialRole as 'TEACHER' | 'STUDENT');
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const { data: boardsRes } = useQuery({
    queryKey: ['boards'],
    queryFn: () => api.get<unknown, { data: Board[] }>('/curriculum/boards'),
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  const [selectedBoardId, setSelectedBoardId] = useState<string | undefined>();

  const { data: subjectsRes } = useQuery({
    queryKey: ['subjects', selectedBoardId],
    queryFn: () =>
      api.get<unknown, { data: Subject[] }>(
        `/curriculum/subjects${selectedBoardId ? `?boardId=${selectedBoardId}` : ''}`,
      ),
    enabled: role === 'TEACHER',
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  const registerMutation = useMutation({
    mutationFn: (data: RegisterForm & { gradeId?: number }) => {
      const { confirmPassword: _confirmPassword, gradeId, gradeIds, ...payload } = data as RegisterForm & { gradeId?: number; gradeIds?: number[] };
      return api.post<unknown, RegisterResponse>('/auth/register', {
        ...payload,
        role,
        gradeIds: role === 'STUDENT'
          ? (gradeId != null ? [gradeId] : [])
          : (gradeIds ?? []),
      });
    },
    onSuccess: (res, variables) => {
      const responseData = res.data as any;
      const user = responseData.user || responseData;
      const { accessToken, refreshToken } = responseData;
      if (user.role === 'STUDENT') {
        setAuth(
          { id: user.id, name: user.name, email: user.email, role: user.role as UserRoleValue },
          accessToken,
          refreshToken,
        );
        message.success('Welcome to CardWise! Your demo cards are ready.');
        navigate('/student');
      } else {
        setRegisteredEmail(variables.email);
        setRegistered(true);
      }
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? 'Registration failed. Please try again.');
    },
  });

  const gradeOptions = GRADES.map((label, idx) => ({ label, value: idx }));

  if (registered) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #ede9fe 100%)', padding: '40px 16px' }}>
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '48px 40px', maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <CheckCircleFilled style={{ fontSize: 56, color: '#10b981', marginBottom: 20 }} />
          <Typography.Title level={3} style={{ margin: 0, marginBottom: 12, color: '#0f172a' }}>Application received!</Typography.Title>
          <Typography.Paragraph style={{ color: '#64748b', fontSize: 15, lineHeight: 1.7, marginBottom: 28 }}>
            Thanks for registering as a teacher. Your account at <strong>{registeredEmail}</strong> is under review.
            Our team will activate it within 1–2 business days.
          </Typography.Paragraph>
          <Button type="primary" block size="large" onClick={() => { window.location.href = LANDING_URL; }} style={{ fontWeight: 700, height: 48, borderRadius: 10 }}>
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #ede9fe 100%)',
        padding: '40px 16px',
      }}
    >
      <div style={{ position: 'absolute', top: 20, left: 24 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => { window.location.href = LANDING_URL; }} style={{ color: '#64748b', fontWeight: 600 }}>Back to home</Button>
      </div>
      <div style={{ width: 460 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: 18,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              marginBottom: 16,
              boxShadow: '0 4px 20px rgba(99,102,241,0.35)',
            }}
          >
            <span style={{ color: 'white', fontWeight: 800, fontSize: 28 }}>C</span>
          </div>
          <Typography.Title level={3} style={{ margin: 0, color: '#0f172a' }}>
            Create your account
          </Typography.Title>
          <Typography.Text style={{ color: '#64748b', fontSize: 14 }}>
            Join CardWise as a teacher or student
          </Typography.Text>
        </div>

        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            padding: '32px 36px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
            <Segmented
              value={role}
              onChange={(val) => setRole(val as 'TEACHER' | 'STUDENT')}
              options={[
                { label: 'I am a Student', value: 'STUDENT' },
                { label: 'I am a Teacher', value: 'TEACHER' },
              ]}
              size="large"
              style={{ fontWeight: 600 }}
            />
          </div>

          <Form
            form={form}
            onFinish={(values: RegisterForm) => registerMutation.mutate(values)}
            layout="vertical"
            requiredMark={(label, { required }) =>
              required ? <>{label}<span style={{ color: '#ef4444', marginLeft: 2 }}>*</span></> : label
            }
          >
            <Form.Item
              name="name"
              label={<span style={{ fontWeight: 600, color: '#374151' }}>Full Name</span>}
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                placeholder="Your full name"
                size="large"
                maxLength={100}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label={<span style={{ fontWeight: 600, color: '#374151' }}>Email</span>}
              rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#94a3b8' }} />}
                placeholder="your@email.com"
                size="large"
                autoComplete="new-email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={{ fontWeight: 600, color: '#374151' }}>Password</span>}
              rules={[
                { required: true, message: 'Password is required' },
                { min: 8, message: 'At least 8 characters' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                placeholder="At least 8 characters"
                size="large"
                maxLength={72}
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={<span style={{ fontWeight: 600, color: '#374151' }}>Confirm Password</span>}
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                placeholder="Re-enter your password"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            {role === 'STUDENT' && (
              <>
                <Form.Item
                  name="parentEmail"
                  dependencies={['email']}
                  label={
                    <span style={{ fontWeight: 600, color: '#374151' }}>
                      Parent&apos;s Email
                    </span>
                  }
                  rules={[
                    { required: true, message: 'Parent email is required for students' },
                    { type: 'email', message: 'Enter a valid email' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || value.toLowerCase() !== getFieldValue('email')?.toLowerCase()) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Parent email must be different from your own email'));
                      },
                    }),
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: '#94a3b8' }} />}
                    placeholder="parent@email.com"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="gradeId"
                  label={<span style={{ fontWeight: 600, color: '#374151' }}>Grade</span>}
                >
                  <Select
                    placeholder="Select your grade"
                    options={gradeOptions}
                    size="large"
                  />
                </Form.Item>
              </>
            )}

            {role === 'TEACHER' && (
              <>
                <Form.Item
                  name="gradeIds"
                  label={<span style={{ fontWeight: 600, color: '#374151' }}>Classes / Grades you teach</span>}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select class(es) — e.g. Grade 6, Grade 7"
                    options={gradeOptions}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontWeight: 600, color: '#374151' }}>Board</span>}
                >
                  <Select
                    placeholder="Select education board"
                    size="large"
                    allowClear
                    value={selectedBoardId}
                    onChange={setSelectedBoardId}
                    options={boardsRes?.data?.map((b: Board) => ({
                      label: b.name,
                      value: b.id,
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  name="subjectIds"
                  label={<span style={{ fontWeight: 600, color: '#374151' }}>Subjects</span>}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select subjects you teach"
                    size="large"
                    options={subjectsRes?.data?.map((s: Subject) => ({
                      label: s.name,
                      value: s.id,
                    }))}
                  />
                </Form.Item>
              </>
            )}

            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={registerMutation.isPending}
              style={{ fontWeight: 700, height: 48, borderRadius: 10, marginTop: 8 }}
            >
              Create Account
            </Button>
          </Form>

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Typography.Text style={{ color: '#64748b' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ fontWeight: 600 }}>
                Sign in
              </Link>
            </Typography.Text>
          </div>
        </div>
      </div>
    </div>
  );
}
